<script setup lang="ts">
// Vue
import { onBeforeMount, ref, watch, computed } from "vue";
import { storeToRefs } from "pinia";
// Types
import { LifetimeTableRow as LifetimeTableRowType } from "@/providers/LifetimeTable/LifetimeTableTypes";

// Providers
import { useProject } from "@/stores/project/project.store";

// Utils
import {
  getHeaders,
  getParents,
  lifetimeTableFilter,
  getParentWithParentId,
  materialGroupings,
  findMaterials,
} from "@/services/Views/MappingWizard/LifeTimeTable";
import BIMFormat from "@/services/Utils/BIMFormat";

import FilterBar from "./FilterBar.vue";
import LifetimeTableRow from "./LifetimeTableRow.vue";
import Table from "@/components/Reusables/Table/Table.vue";

const projectStore = useProject();
const { lifetimeTable } = storeToRefs(projectStore);

const props = defineProps<{
  focusConstructionId?: string;
  focusConstructionIdAlternate?: string;
}>();

const rows = ref<LifetimeTableRowType[]>();
const mounted = ref<boolean>(false);
const headers = ref<string[]>();
const parents = ref<string[]>([]);
const materialGroups = ref<string[]>([]);
const selectedMaterialGroup = ref<string>("All materials selected");
const parentsWithId = ref<string[]>([]);
const selectedMaterials = ref<string[]>(["All materials selected"]);
const selectedStructure = ref<string>("All structures selected");
const selectedStructureWithId = ref<string>("All structures selected");
const filterbarKey = ref<number>(0);

const emits = defineEmits<{
  close: [payload: { value: number; constructionIdAlternate: string }];
  cancel: [];
}>();

const hoveredRowIndex = ref<number | null>(null);
const hoveredColIndex = ref<number | null>(null);

const handleCellHover = ({ rowIndex, colIndex }: { rowIndex: number; colIndex: number }) => {
  hoveredRowIndex.value = rowIndex;
  hoveredColIndex.value = colIndex;
};

function isSafari() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

const resetTable = (button?: boolean) => {
  selectedMaterials.value = ["All materials selected"];
  selectedStructure.value = "All structures selected";
  mounted.value = false;

  if (!lifetimeTable.value) return;
  rows.value = lifetimeTable.value.rows;
  headers.value = getHeaders(lifetimeTable.value.rows);
  parents.value = getParents(lifetimeTable.value.rows);
  parentsWithId.value = getParentWithParentId(lifetimeTable.value.rows);
  materialGroups.value = materialGroupings;
  selectedMaterialGroup.value = "All materials selected";
  selectedStructureWithId.value = "All structures selected";
  if (button) {
    filterbarKey.value++;
  }
  mounted.value = true;
};

const handleLifeTimeSelected = (props: { value: number; constructionIdAlternate: string }) => {
  emits("close", props);
};

const handleSelectMaterial = async (material: string) => {
  if (!rows.value) return;
  if (material === "All materials selected") {
    resetTable();
    return;
  }

  const foundMaterials = findMaterials(material, rows.value);
  if (!foundMaterials) return;
  selectedMaterials.value = foundMaterials;
  selectedMaterialGroup.value = material;
};

const handleSelectStructure = async (structure: string) => {
  if (structure === "All structures selected") {
    resetTable();
    return;
  }
  const stringToCompare = structure.replace(/^[^a-zA-Z]+/, "");
  const foundStructure = parents.value?.find((parent) => parent.replace(/^[^a-zA-Z]+/, "") === stringToCompare);
  if (!foundStructure) return;
  selectedStructure.value = foundStructure;
  selectedStructureWithId.value = structure;
};

watch([selectedMaterials, selectedStructure], ([_newMat, _newStruc], [_oldMat, _oldStruc]) => {
  if (!lifetimeTable.value) return;
  const filteredRows = lifetimeTableFilter(lifetimeTable.value?.rows, selectedMaterials.value, selectedStructure.value);
  rows.value = filteredRows.rows;
  parentsWithId.value = filteredRows.parentsWithId;
  materialGroups.value = filteredRows.materialGroups;
  if (selectedMaterials.value[0] !== "All materials selected") {
    headers.value = selectedMaterials.value;
  } else {
    headers.value = getHeaders(rows.value);
  }
});

const safari = computed(() => {
  return isSafari();
});

const allowFocusedAlternate = computed(() => {
  return props.focusConstructionId !== props.focusConstructionIdAlternate;
});

onBeforeMount(async () => {
  // Reset
  resetTable();

  // Check for focused row
  if (props.focusConstructionId || props.focusConstructionIdAlternate) {
    // Prioritize alternate id
    const focusConstructionId = props.focusConstructionIdAlternate || props.focusConstructionId;

    // Scroll
    setTimeout(() => {
      const focusRow = document.getElementById(`#${focusConstructionId}`);
      if (focusRow) {
        focusRow.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    }, 200);
  }
});
</script>

<template>
  <div class="lifetime-table-wrapper text-text-color flex w-full flex-col">
    <div
      class="bg-elevated sticky left-0 z-50 w-full px-4 pb-2"
      style="top: -36px">
      <!-- style kept: negative 36px offset not expressible with standard Tailwind spacing tokens -->
      <FilterBar
        :key="filterbarKey"
        :parents="parentsWithId"
        :material-groupings="materialGroups"
        :selected-mat="selectedMaterialGroup"
        :selected-struc="selectedStructureWithId"
        @select-material="handleSelectMaterial"
        @select-structure="handleSelectStructure"
        @reset-table="resetTable" />
    </div>

    <!-- Scrollable Table Container -->
    <div
      v-dragscroll
      class="table-container rounded-16 w-full">
      <Table
        caption="Lifetime table"
        table-class="min-w-max border-separate border-spacing-0">
        <template #thead>
          <thead>
            <tr>
              <th class="z-index w-4 align-bottom">
                <div class="flex flex-col gap-2 text-left text-sm">
                  <div
                    v-if="focusConstructionId"
                    class="flex gap-2">
                    <span class="rounded-8 bg-app-green-200 w-12"></span>Real-Time LCA suggestion
                  </div>
                  <div
                    v-if="focusConstructionIdAlternate"
                    class="flex gap-2">
                    <span class="rounded-8 bg-app-yellow-100 w-12"></span>Your selection
                  </div>
                </div>
              </th>
              <th
                v-for="(header, colIndex) in headers"
                :key="colIndex"
                class="vertical-text sticky-th text-sm"
                :class="[
                  {
                    'rotate-180 transform': !safari,
                  },
                  hoveredColIndex === colIndex ? 'bg-app-blue-50!' : '',
                ]">
                <span>{{ header }}</span>
              </th>
            </tr>
          </thead>
        </template>

        <template #tbody>
          <tbody>
            <tr
              v-for="(row, rowIndex) in rows"
              :key="row.constructionId">
              <LifetimeTableRow
                :headers="headers"
                :row="row"
                :row-index="rowIndex"
                :hovered-row-index="hoveredRowIndex"
                :hovered-col-index="hoveredColIndex"
                :focused="focusConstructionId === BIMFormat(row.constructionId)"
                :focused-alternate="allowFocusedAlternate && focusConstructionIdAlternate === BIMFormat(row.constructionId)"
                @life-time-selected="handleLifeTimeSelected"
                @cell-hover="handleCellHover" />
            </tr>
          </tbody>
        </template>
      </Table>
    </div>
  </div>
</template>

<style scoped>
.table-container {
  overflow-x: auto;
  overflow-y: auto;
  max-height: calc(100vh - 300px);
  -webkit-overflow-scrolling: touch;
  width: 100%;
}

.lifetime-table-wrapper {
  width: 90vw;
}

/* th/td scoped styles apply to slot content rendered in this component's scope */
th,
td {
  padding: 8px;
  outline: 1px solid var(--color-element-stroke); /* migrated to token – was #e3e8ee */
}

thead th {
  background-color: var(--color-surface) !important; /* migrated to token – was #f3f6f5 */
  position: -webkit-sticky !important;
  position: sticky !important;
  top: 0 !important;
  z-index: 30 !important;
}

thead th.z-index {
  position: -webkit-sticky !important;
  position: sticky !important;
  top: 0 !important;
  left: 0 !important;
  z-index: 40 !important;
  background-color: var(--color-surface) !important; /* migrated to token – was #f3f6f5 */
}

.sticky-th {
  background-color: var(--color-surface); /* migrated to token – was #f3f6f5 */
}

tbody td:first-child {
  padding-top: 0;
  padding-bottom: 0;
  position: -webkit-sticky !important;
  position: sticky !important;
  z-index: 10 !important;
  left: 0 !important;
  background-color: var(--color-surface) !important; /* migrated to token – was #f3f6f5 */
}

.z-index {
  z-index: 40;
  background-color: var(--color-surface); /* migrated to token – was #f3f6f5 */
}

.vertical-text {
  writing-mode: vertical-lr;
  text-align: start;
}
</style>
